import React from "react";

interface SeekBarProps {
    /** Fill percentage from 0–100. */
    pct: number;
    /** Whether the associated input is focused — shows tick marks when true. */
    focused?: boolean;
    /** Tick positions as percentages (e.g., [0, 25, 50, 75, 100]). */
    ticks?: number[];
    /** Called with the nearest tick percentage when the bar is clicked. */
    onSeek?: (pct: number) => void;
    /** Optional className on the outer container. */
    className?: string;
}

/** How long the charcoal bar takes to sweep across on focus / retract on blur. */
const BAR_DURATION_MS = 400;
/** Fade-in/out duration for each individual tick. */
const TICK_FADE_MS = 150;

/**
 * @version 2.2.0
 * @published 2026-04-16
 * @history
 *   2.2.0 — Restored ember fill showing % of max balance on focus.
 *           Renders above the charcoal overlay so the filled portion
 *           reads as ember. Ticks are ember when inside the fill,
 *           charcoal otherwise.
 *   2.1.0 — Added focus/blur animation (charcoal scaleX + staggered ticks).
 *   2.0.0 — Monochrome treatment. Ember fill removed.
 *   1.0.0 — Initial publish. Ember fill with accent/line-base tick coloring.
 */
export function SeekBar({
    pct,
    focused = false,
    ticks = [0, 25, 50, 75, 100],
    onSeek,
    className
}: SeekBarProps): React.ReactElement {
    const clamped = Math.max(0, Math.min(100, pct));

    const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        if (!onSeek) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const clickPct = ((e.clientX - rect.left) / rect.width) * 100;
        const nearest = ticks.reduce((a, b) =>
            Math.abs(b - clickPct) < Math.abs(a - clickPct) ? b : a
        );
        onSeek(nearest);
    };

    return (
        <div
            className={`relative cursor-pointer py-2 -my-2 ${className ?? ""}`}
            onMouseDown={handleClick}
        >
            {/* Gray baseline — always visible. */}
            <div className="h-px bg-line-base" />

            {/* Charcoal overlay — grows left→right on focus, retracts right→left on blur. */}
            <div
                className="absolute top-2 left-0 right-0 h-px bg-foreground-base"
                style={{
                    transformOrigin: "left",
                    transform: focused ? "scaleX(1)" : "scaleX(0)",
                    transition: `transform ${BAR_DURATION_MS}ms ease-out`
                }}
            />

            {/* Ember fill — shows % of max balance. Sits above the charcoal
                overlay so the filled portion reads as ember. Only visible
                when focused and pct > 0. Width transitions smoothly as
                the user types. */}
            <div
                className="absolute top-2 left-0 h-px bg-accent"
                style={{
                    width: `${clamped}%`,
                    opacity: focused && clamped > 0 ? 1 : 0,
                    transition: `width 300ms ease-out, opacity ${TICK_FADE_MS}ms ease-out`
                }}
            />

            {/* Tick marks — staggered fade, colored by fill position.
                Ember when inside the filled area, charcoal otherwise. */}
            {ticks.map((tick) => {
                const delayIn = Math.round((tick / 100) * BAR_DURATION_MS);
                const delayOut = Math.round(
                    ((100 - tick) / 100) * BAR_DURATION_MS
                );
                const delay = focused ? delayIn : delayOut;
                const isFilled = focused && clamped >= tick && clamped > 0;
                return (
                    <div
                        key={tick}
                        className="absolute top-1/2 -translate-y-1/2 w-px h-2"
                        style={{
                            left: `${tick}%`,
                            backgroundColor: isFilled
                                ? "rgb(var(--color-accent-base))"
                                : "rgb(var(--color-foreground-base))",
                            opacity: focused ? 1 : 0,
                            transition: `opacity ${TICK_FADE_MS}ms ease-out ${delay}ms, background-color 200ms ease-out`
                        }}
                    />
                );
            })}
        </div>
    );
}
